import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, UserCheck, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Check, X, Minus, DollarSign } from 'lucide-react';
import PhotoCapture from '@/components/PhotoCapture';
import ChildAvatar from '@/components/ChildAvatar';
import { useAuth } from '@/contexts/AuthContext';

const roleLabels = {
  instrutor: 'Instrutor',
  educador: 'Educador',
  coordenador: 'Coordenador',
  apoiador: 'Apoiador',
  psicologa: 'Psicóloga',
  assistente_social: 'Assistente Social',
  outro: 'Outro'
};

const Professionals = () => {
  const { canEdit, isAdmin } = useAuth();
  const canEditProfessionals = canEdit('professionals') || isAdmin();
  
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    photo_url: '',
    role: 'instrutor',
    specialty: '',
    phone: '',
    email: '',
    has_schedule: true,
    create_user: false,
    user_password: ''
  });

  // Valor por aula e aulas por dia/turno
  const [periodValue, setPeriodValue] = useState(0);
  const [classesConfig, setClassesConfig] = useState({}); // {"terca_Matutino": 3, "quarta_Vespertino": 2, ...}
  const [savingValue, setSavingValue] = useState(false);

  // Agenda do instrutor
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleProf, setScheduleProf] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleMonth, setScheduleMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    fetchProfessionals();
  }, []);

  const fetchProfessionals = async () => {
    try {
      const response = await axios.get(`${API}/professionals`);
      setProfessionals(response.data);
    } catch (error) {
      toast.error('Erro ao carregar profissionais');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProfessional) {
        await axios.put(`${API}/professionals/${editingProfessional.id}`, formData);
        toast.success('Profissional atualizado com sucesso!');
      } else {
        await axios.post(`${API}/professionals`, formData);
        toast.success('Profissional cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      fetchProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (professional) => {
    setEditingProfessional(professional);
    setFormData({ ...professional, has_schedule: professional.has_schedule ?? (professional.role === 'instrutor') });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir este profissional?')) {
      try {
        await axios.delete(`${API}/professionals/${id}`);
        toast.success('Profissional removido com sucesso!');
        fetchProfessionals();
      } catch (error) {
        toast.error('Erro ao remover profissional');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      birth_date: '',
      photo_url: '',
      role: 'instrutor',
      specialty: '',
      phone: '',
      email: '',
      create_user: false,
      user_password: '',
      has_schedule: true
    });
    setEditingProfessional(null);
  };

  const openSchedule = async (prof) => {
    setScheduleProf(prof);
    setScheduleOpen(true);
    await fetchSchedule(prof.id, scheduleMonth);
  };

  const fetchSchedule = async (profId, month) => {
    try {
      const response = await axios.get(`${API}/professionals/${profId}/schedule?month=${month}`);
      setScheduleData(response.data);
      setPeriodValue(response.data.professional?.period_value || 0);
      setClassesConfig(response.data.professional?.classes_config || {});
    } catch (error) {
      toast.error('Erro ao carregar agenda');
    }
  };

  const changeScheduleMonth = async (delta) => {
    const [y, m] = scheduleMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const newMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setScheduleMonth(newMonth);
    if (scheduleProf) await fetchSchedule(scheduleProf.id, newMonth);
  };

  const savePeriodValue = async (value) => {
    if (!scheduleProf) return;
    setSavingValue(true);
    try {
      await axios.put(`${API}/professionals/${scheduleProf.id}/period-value`, { 
        period_value: value,
        classes_config: classesConfig
      });
      toast.success('Configurações salvas!');
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSavingValue(false);
    }
  };

  const toggleAttendance = async (date, period, currentValue, isExpected) => {
    if (!scheduleProf) return;
    const existing = scheduleData?.attendance?.[date] || {};
    
    let newValue;
    if (isExpected) {
      // Dia previsto: null (presente por default) → false (ausente) → null
      if (currentValue === null || currentValue === undefined) newValue = false;
      else newValue = null;
    } else {
      // Dia NÃO previsto: null (sem registro) → true (presente extra) → null
      if (currentValue === null || currentValue === undefined) newValue = true;
      else newValue = null;
    }

    const payload = {
      manha: period === 'manha' ? newValue : (existing.manha ?? null),
      tarde: period === 'tarde' ? newValue : (existing.tarde ?? null),
      noturno: period === 'noturno' ? newValue : (existing.noturno ?? null),
      observations: existing.observations || ''
    };

    try {
      await axios.put(`${API}/professionals/${scheduleProf.id}/attendance/${date}`, payload);
      // Atualizar local
      setScheduleData(prev => ({
        ...prev,
        attendance: {
          ...prev.attendance,
          [date]: { ...existing, ...payload, date }
        }
      }));
    } catch (error) {
      toast.error('Erro ao registrar presença');
    }
  };

  // Gerar dias do calendário
  const getCalendarDays = () => {
    const [y, m] = scheduleMonth.split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1);
    const lastDay = new Date(y, m, 0);
    const startDow = firstDay.getDay(); // 0=dom
    const days = [];
    // Dias vazios antes
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateObj = new Date(y, m - 1, d);
      const dow = dateObj.getDay();
      const dayMap = { 0: 'domingo', 1: 'segunda', 2: 'terca', 3: 'quarta', 4: 'quinta', 5: 'sexta', 6: 'sabado' };
      days.push({
        day: d,
        date: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
        dayOfWeek: dayMap[dow],
        isWeekend: dow === 0 || dow === 6
      });
    }
    return days;
  };

  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  return (
    <div className="space-y-6" data-testid="professionals-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Profissionais</h1>
          <p className="text-slate-600 mt-1">{canEditProfessionals ? 'Gerencie instrutores e educadores' : 'Visualização de profissionais'}</p>
        </div>
        {canEditProfessionals && (
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-professional-button" className="bg-[#D87A4E] hover:bg-[#c26a42]">
              <Plus className="h-4 w-4 mr-2" />
              Novo Profissional
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProfessional ? 'Editar Profissional' : 'Novo Profissional'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pb-4">
              {/* Foto */}
              <div className="border-b pb-4">
                <Label>Foto do Profissional</Label>
                <PhotoCapture
                  currentPhoto={formData.photo_url}
                  onPhotoCapture={(photo) => setFormData({ ...formData, photo_url: photo })}
                  onPhotoRemove={() => setFormData({ ...formData, photo_url: '' })}
                />
              </div>

              <div>
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  data-testid="prof-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="birth_date">Data de Nascimento</Label>
                <Input
                  id="birth_date"
                  type="date"
                  data-testid="prof-birthdate-input"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="role">Função *</Label>
                <select
                  id="role"
                  data-testid="prof-role-select"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="instrutor">Instrutor</option>
                  <option value="educador">Educador</option>
                  <option value="coordenador">Coordenador</option>
                  <option value="apoiador">Apoiador</option>
                  <option value="psicologa">Psicóloga</option>
                  <option value="assistente_social">Assistente Social</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_schedule"
                  data-testid="prof-has-schedule-checkbox"
                  checked={formData.has_schedule ?? false}
                  onChange={(e) => setFormData({ ...formData, has_schedule: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <Label htmlFor="has_schedule" className="text-sm font-medium cursor-pointer">
                  Habilitar Agenda para este profissional
                </Label>
              </div>
              <div>
                <Label htmlFor="specialty">Especialidade</Label>
                <Input
                  id="specialty"
                  data-testid="prof-specialty-input"
                  value={formData.specialty}
                  onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                  placeholder="Ex: Muay Thai, Capoeira, Música..."
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefone *</Label>
                <Input
                  id="phone"
                  data-testid="prof-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="prof-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              
              {/* Opção de criar usuário - só mostra quando não está editando */}
              {!editingProfessional && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <input
                      type="checkbox"
                      id="create_user"
                      checked={formData.create_user}
                      onChange={(e) => setFormData({ ...formData, create_user: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-[#D87A4E] focus:ring-[#D87A4E]"
                    />
                    <Label htmlFor="create_user" className="text-sm font-medium cursor-pointer">
                      Criar acesso ao sistema para este profissional
                    </Label>
                  </div>
                  
                  {formData.create_user && (
                    <div className="pl-6 space-y-3 bg-orange-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-600">
                        O email informado acima será usado como login. {formData.email ? '' : '⚠️ Preencha o email acima.'}
                      </p>
                      <div>
                        <Label htmlFor="user_password">Senha inicial</Label>
                        <Input
                          id="user_password"
                          type="password"
                          placeholder="Deixe vazio para usar 'mudar123'"
                          value={formData.user_password}
                          onChange={(e) => setFormData({ ...formData, user_password: e.target.value })}
                        />
                        <p className="text-xs text-slate-500 mt-1">Recomende ao profissional trocar a senha no primeiro acesso.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-professional-button" className="bg-[#D87A4E] hover:bg-[#c26a42]">
                  {editingProfessional ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D87A4E]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="professionals-list">
          {professionals.map((prof) => (
            <div
              key={prof.id}
              className="bg-white rounded-xl shadow-md p-6 border border-slate-100 card-hover"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <ChildAvatar 
                    photo={prof.photo_url} 
                    name={prof.name} 
                    size="lg"
                    zoomable={true}
                    className="border-2 border-purple-200"
                  />
                  <div>
                    <h3 className="font-semibold text-slate-800">{prof.name}</h3>
                    <p className="text-xs text-slate-500">{roleLabels[prof.role] || prof.role}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {prof.specialty && (
                  <div>
                    <span className="text-slate-600">Especialidade:</span>
                    <p className="font-medium text-slate-800">{prof.specialty}</p>
                  </div>
                )}
                <div>
                  <span className="text-slate-600">Telefone:</span>
                  <p className="font-medium text-slate-800">{prof.phone}</p>
                </div>
                {prof.email && (
                  <div>
                    <span className="text-slate-600">Email:</span>
                    <p className="font-medium text-slate-800 text-xs">{prof.email}</p>
                  </div>
                )}
                {prof.user_id && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <UserCheck className="h-3 w-3 mr-1" />
                      Usuário do Sistema
                    </span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                {(prof.has_schedule ?? (prof.role === 'instrutor')) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-indigo-600 hover:bg-indigo-50"
                    onClick={() => openSchedule(prof)}
                    data-testid={`schedule-prof-${prof.id}`}
                  >
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    Agenda
                  </Button>
                )}
                {canEditProfessionals && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEdit(prof)}
                      data-testid={`edit-prof-${prof.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(prof.id)}
                      data-testid={`delete-prof-${prof.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && professionals.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
          <UserCheck className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Nenhum profissional cadastrado</p>
        </div>
      )}

      {/* Modal de Agenda do Instrutor */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-indigo-600" />
              Agenda - {scheduleProf?.name}
            </DialogTitle>
          </DialogHeader>

          {scheduleData && (
            <div className="space-y-4 mt-2">
              {/* Atividades vinculadas */}
              {scheduleData.activities?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {scheduleData.activities.map(act => (
                    <span key={act.id} className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                      {act.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Navegação do mês */}
              <div className="flex items-center justify-between">
                <button onClick={() => changeScheduleMonth(-1)} className="p-1.5 hover:bg-slate-100 rounded">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <h3 className="font-semibold text-slate-800">
                  {monthNames[parseInt(scheduleMonth.split('-')[1]) - 1]} {scheduleMonth.split('-')[0]}
                </h3>
                <button onClick={() => changeScheduleMonth(1)} className="p-1.5 hover:bg-slate-100 rounded">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* Legenda */}
              <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500 inline-block" /> Presente</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500 inline-block" /> Ausente</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-indigo-100 border border-indigo-300 inline-block" /> Dia previsto</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-100 inline-block" /> Sem previsão</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-200 border border-rose-400 inline-block" /> Feriado/Sem atend.</span>
              </div>

              {/* Valor por aula e configuração de aulas por dia/turno */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 shrink-0">
                    <DollarSign className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-800">Valor/aula:</span>
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm text-amber-700">R$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      data-testid="period-value-input"
                      className="h-8 w-28 text-sm"
                      value={periodValue || ''}
                      onChange={(e) => setPeriodValue(parseFloat(e.target.value) || 0)}
                      placeholder="0,00"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                      disabled={savingValue}
                      data-testid="save-period-value-btn"
                      onClick={() => savePeriodValue(periodValue)}
                    >
                      {savingValue ? '...' : 'Salvar'}
                    </Button>
                  </div>
                </div>
                {/* Aulas por dia/turno */}
                {scheduleData.expected_days && Object.keys(scheduleData.expected_days).length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-amber-800 block mb-1.5">Aulas por dia/turno:</span>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const dayOrder = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
                        const dayLabels = { segunda: 'Seg', terca: 'Ter', quarta: 'Qua', quinta: 'Qui', sexta: 'Sex', sabado: 'Sáb', domingo: 'Dom' };
                        const shiftKeys = { 'Matutino': 'M', 'Vespertino': 'T', 'Noturno': 'N' };
                        return Object.entries(scheduleData.expected_days)
                          .sort(([a], [b]) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
                          .map(([day, shifts]) => {
                        const shiftOrder = { 'Matutino': 0, 'Vespertino': 1, 'Noturno': 2 };
                        return [...shifts].sort((a, b) => (shiftOrder[a] ?? 9) - (shiftOrder[b] ?? 9)).map(shift => {
                          const configKey = `${day}_${shift}`;
                          return (
                            <div key={configKey} className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-amber-200">
                              <span className="text-[10px] text-amber-800 font-medium">{dayLabels[day] || day} {shiftKeys[shift] || shift}:</span>
                              <select
                                className="h-6 w-10 text-xs rounded border border-amber-300 bg-white text-center"
                                data-testid={`classes-${configKey}`}
                                value={classesConfig[configKey] ?? 3}
                                onChange={(e) => setClassesConfig(prev => ({ ...prev, [configKey]: parseInt(e.target.value) }))}
                              >
                                <option value={1}>1</option>
                                <option value={2}>2</option>
                                <option value={3}>3</option>
                              </select>
                            </div>
                          );
                        });
                      });
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Contador de saldo e financeiro */}
              {(() => {
                let extras = 0;
                let faltas = 0;
                let totalExpectedClasses = 0;
                const today = new Date().toISOString().split('T')[0];
                const allDays = getCalendarDays().filter(d => d);
                const pastDays = allDays.filter(d => d.date <= today);
                const blocked = scheduleData.blocked_dates || {};
                const cc = classesConfig || {};

                const getClasses = (dayOfWeek, shift) => cc[`${dayOfWeek}_${shift}`] ?? 3;

                // Contar aulas previstas no mês (excluindo bloqueados)
                allDays.forEach(dayInfo => {
                  if (blocked[dayInfo.date]) return;
                  const expectedShifts = scheduleData.expected_days?.[dayInfo.dayOfWeek] || [];
                  expectedShifts.forEach(s => {
                    totalExpectedClasses += getClasses(dayInfo.dayOfWeek, s);
                  });
                });

                // Contar extras e faltas até hoje (em aulas por dia/turno)
                pastDays.forEach(dayInfo => {
                  if (blocked[dayInfo.date]) return;
                  const expectedShifts = scheduleData.expected_days?.[dayInfo.dayOfWeek] || [];
                  const att = scheduleData.attendance?.[dayInfo.date] || {};
                  const isExpM = expectedShifts.includes('Matutino');
                  const isExpT = expectedShifts.includes('Vespertino');
                  const isExpN = expectedShifts.includes('Noturno');
                  if (att.manha === true && !isExpM) extras += getClasses(dayInfo.dayOfWeek, 'Matutino');
                  if (att.manha === false && isExpM) faltas += getClasses(dayInfo.dayOfWeek, 'Matutino');
                  if (att.tarde === true && !isExpT) extras += getClasses(dayInfo.dayOfWeek, 'Vespertino');
                  if (att.tarde === false && isExpT) faltas += getClasses(dayInfo.dayOfWeek, 'Vespertino');
                  if (att.noturno === true && !isExpN) extras += getClasses(dayInfo.dayOfWeek, 'Noturno');
                  if (att.noturno === false && isExpN) faltas += getClasses(dayInfo.dayOfWeek, 'Noturno');
                });

                const saldo = extras - faltas;
                const classesWorked = totalExpectedClasses - faltas + extras;
                const totalValue = classesWorked * (periodValue || 0);
                const baseValue = totalExpectedClasses * (periodValue || 0);

                return (
                  <div className="space-y-2">
                    {/* Placar de ausências, extras e saldo */}
                    <div className="flex items-center justify-center gap-4 py-2 px-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-red-700 font-medium">Ausências:</span>
                        <span className={`text-sm font-bold ${faltas > 0 ? 'text-red-600' : 'text-slate-400'}`}>{faltas}</span>
                      </div>
                      <div className="w-px h-5 bg-slate-300" />
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-green-700 font-medium">Extras:</span>
                        <span className={`text-sm font-bold ${extras > 0 ? 'text-green-600' : 'text-slate-400'}`}>{extras}</span>
                      </div>
                      <div className="w-px h-5 bg-slate-300" />
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-slate-600 font-medium">Saldo:</span>
                        <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${
                          saldo > 0 ? 'bg-green-100 text-green-700' :
                          saldo < 0 ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>{saldo > 0 ? `+${saldo}` : saldo}</span>
                      </div>
                    </div>

                    {/* Aulas e valor financeiro */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center p-2 bg-indigo-50 rounded-lg border border-indigo-200">
                        <p className="text-[10px] text-indigo-600 font-medium uppercase">Aulas Previstas</p>
                        <p className="text-lg font-bold text-indigo-800" data-testid="expected-periods">{totalExpectedClasses}</p>
                      </div>
                      <div className="text-center p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-[10px] text-emerald-600 font-medium uppercase">Aulas Trabalhadas</p>
                        <p className="text-lg font-bold text-emerald-800" data-testid="worked-periods">{classesWorked}</p>
                      </div>
                      <div className="text-center p-2 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-[10px] text-amber-600 font-medium uppercase">Valor Total</p>
                        <p className={`text-lg font-bold ${totalValue !== baseValue ? (totalValue > baseValue ? 'text-green-700' : 'text-red-700') : 'text-amber-800'}`} data-testid="total-value">
                          {periodValue > 0 ? `R$ ${totalValue.toFixed(2).replace('.', ',')}` : '—'}
                        </p>
                        {periodValue > 0 && totalValue !== baseValue && (
                          <p className="text-[9px] text-slate-500">
                            Base: R$ {baseValue.toFixed(2).replace('.', ',')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Calendário */}
              <div className="border rounded-lg overflow-hidden">
                <div className="grid grid-cols-7 bg-slate-50 border-b">
                  {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                    <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {getCalendarDays().map((dayInfo, idx) => {
                    if (!dayInfo) return <div key={`empty-${idx}`} className="min-h-[88px] border-b border-r bg-slate-50" />;

                    const blocked = scheduleData.blocked_dates || {};
                    const isBlocked = !!blocked[dayInfo.date];
                    const expectedShifts = scheduleData.expected_days?.[dayInfo.dayOfWeek] || [];
                    const isExpected = expectedShifts.length > 0;
                    const attendance = scheduleData.attendance?.[dayInfo.date] || {};
                    const today = new Date().toISOString().split('T')[0] === dayInfo.date;

                    const getStatus = (period) => {
                      const val = attendance[period];
                      if (val === true) return 'present';
                      if (val === false) return 'absent';
                      return null;
                    };

                    const isExpectedManha = expectedShifts.includes('Matutino');
                    const isExpectedTarde = expectedShifts.includes('Vespertino');
                    const isExpectedNoturno = expectedShifts.includes('Noturno');

                    if (isBlocked) {
                      return (
                        <div
                          key={dayInfo.date}
                          className={`min-h-[88px] border-b border-r p-1 bg-rose-100 ${today ? 'ring-2 ring-inset ring-indigo-400' : ''}`}
                          title={blocked[dayInfo.date]}
                        >
                          <div className="text-xs text-rose-400 mb-1 text-center line-through">{dayInfo.day}</div>
                          <div className="flex items-center justify-center h-8">
                            <span className="text-[8px] text-rose-500 text-center leading-tight px-0.5 font-medium">
                              {blocked[dayInfo.date].length > 15 ? blocked[dayInfo.date].slice(0, 15) + '…' : blocked[dayInfo.date]}
                            </span>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={dayInfo.date}
                        className={`min-h-[88px] border-b border-r p-1 ${
                          dayInfo.isWeekend && !isExpected ? 'bg-slate-50' : isExpected ? 'bg-indigo-50/40' : 'bg-white'
                        } ${today ? 'ring-2 ring-inset ring-indigo-400' : ''}`}
                      >
                        <div className="text-xs text-slate-500 mb-1 text-center">{dayInfo.day}</div>
                        <div className="flex flex-col gap-0.5">
                          {/* Manhã */}
                          <button
                            onClick={() => toggleAttendance(dayInfo.date, 'manha', attendance.manha, isExpectedManha)}
                            className={`w-full text-center text-[10px] py-0.5 rounded transition-colors ${
                              getStatus('manha') === 'present' ? 'bg-green-500 text-white' :
                              getStatus('manha') === 'absent' ? 'bg-red-500 text-white' :
                              isExpectedManha ? 'bg-indigo-100 text-indigo-600 hover:bg-red-100' :
                              'bg-slate-100 text-slate-400 hover:bg-green-100'
                            }`}
                            title={`Manhã - ${getStatus('manha') === 'present' ? 'Presente (extra)' : getStatus('manha') === 'absent' ? 'Ausente' : isExpectedManha ? 'Previsto (clique p/ marcar ausência)' : 'Clique p/ marcar presença extra'}`}
                          >
                            {getStatus('manha') === 'present' ? 'M ✓' :
                             getStatus('manha') === 'absent' ? 'M ✗' : 'M'}
                          </button>
                          {/* Tarde */}
                          <button
                            onClick={() => toggleAttendance(dayInfo.date, 'tarde', attendance.tarde, isExpectedTarde)}
                            className={`w-full text-center text-[10px] py-0.5 rounded transition-colors ${
                              getStatus('tarde') === 'present' ? 'bg-green-500 text-white' :
                              getStatus('tarde') === 'absent' ? 'bg-red-500 text-white' :
                              isExpectedTarde ? 'bg-indigo-100 text-indigo-600 hover:bg-red-100' :
                              'bg-slate-100 text-slate-400 hover:bg-green-100'
                            }`}
                            title={`Tarde - ${getStatus('tarde') === 'present' ? 'Presente (extra)' : getStatus('tarde') === 'absent' ? 'Ausente' : isExpectedTarde ? 'Previsto (clique p/ marcar ausência)' : 'Clique p/ marcar presença extra'}`}
                          >
                            {getStatus('tarde') === 'present' ? 'T ✓' :
                             getStatus('tarde') === 'absent' ? 'T ✗' : 'T'}
                          </button>
                          {/* Noturno */}
                          <button
                            onClick={() => toggleAttendance(dayInfo.date, 'noturno', attendance.noturno, isExpectedNoturno)}
                            className={`w-full text-center text-[10px] py-0.5 rounded transition-colors ${
                              getStatus('noturno') === 'present' ? 'bg-green-500 text-white' :
                              getStatus('noturno') === 'absent' ? 'bg-red-500 text-white' :
                              isExpectedNoturno ? 'bg-indigo-100 text-indigo-600 hover:bg-red-100' :
                              'bg-slate-100 text-slate-400 hover:bg-green-100'
                            }`}
                            title={`Noturno - ${getStatus('noturno') === 'present' ? 'Presente (extra)' : getStatus('noturno') === 'absent' ? 'Ausente' : isExpectedNoturno ? 'Previsto (clique p/ marcar ausência)' : 'Clique p/ marcar presença extra'}`}
                          >
                            {getStatus('noturno') === 'present' ? 'N ✓' :
                             getStatus('noturno') === 'absent' ? 'N ✗' : 'N'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="text-xs text-slate-400 text-center">
                Dias previstos: clique para marcar ausência. Dias sem previsão: clique para marcar presença extra.
              </p>
            </div>
          )}

          {!scheduleData && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Professionals;